/**
 * Plan Details Card Web Component
 *
 * Displays plan details including plan name, data amount, speed, text allowance, and voice allowance.
 *
 * Usage:
 * <plan-details-card
 *   plan-name="..."
 *   data-amount="..."
 *   speed="..."
 *   text-allowance="..."
 *   voice-allowance="..."
 *   show-plan-name="true"
 *   show-data-amount="true"
 *   show-speed="true"
 *   show-text-allowance="true"
 *   show-voice-allowance="true"
 *   plan-name-order="1"
 *   data-amount-order="2"
 *   speed-order="3"
 *   text-allowance-order="4"
 *   voice-allowance-order="5"
 *   label-plan-name="Plan"
 *   label-data-amount="Data"
 *   label-speed="Speed"
 *   label-text-allowance="SMS"
 *   label-voice-allowance="Voice"
 *   card-title="Plan Details"
 *   card-title-text-color="#000000"
 *   card-title-text-size="20"
 *   card-title-text-weight="600"
 *   container-bg-color="#FFFFFF"
 *   container-bg-transparent="false"
 *   container-padding="20"
 *   container-border-radius="8"
 *   text-color="#000000"
 *   text-size="16"
 *   text-weight="400"
 *   list-gap="12"
 *   show-bullet="true"
 *   bullet-type="disc"
 * ></plan-details-card>
 */

class PlanDetailsCard extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback() {
    this.render();
  }

  static get observedAttributes() {
    return [
      'plan-name', 'data-amount', 'speed', 'text-allowance', 'voice-allowance', 'autorenew-period',
      'show-plan-name', 'show-data-amount', 'show-speed', 'show-text-allowance', 'show-voice-allowance', 'show-autorenew-period',
      'plan-name-order', 'data-amount-order', 'speed-order', 'text-allowance-order', 'voice-allowance-order', 'autorenew-period-order',
      'label-plan-name', 'label-data-amount', 'label-speed', 'label-text-allowance', 'label-voice-allowance',
      'card-title', 'card-title-text-color', 'card-title-text-size', 'card-title-text-weight',
      'container-bg-color', 'container-bg-transparent', 'container-padding', 'container-border-radius',
      'text-color', 'text-size', 'text-weight',
      'list-gap', 'show-bullet', 'bullet-type',
      'no-plan-mode', 'no-plan-icon', 'no-plan-icon-size', 'no-plan-icon-color', 'no-plan-title', 'no-plan-title-size',
      'no-plan-title-weight', 'no-plan-title-color', 'no-plan-text', 'no-plan-text-show', 'no-plan-text-size',
      'no-plan-text-weight', 'no-plan-text-color', 'no-plan-text-max-width', 'no-plan-button-url',
      'no-plan-button-text', 'no-plan-button-icon-show', 'no-plan-button-gap',
      'no-plan-button-padding-v', 'no-plan-button-padding-h', 'no-plan-button-bg', 'no-plan-button-bg-transparent',
      'no-plan-button-text-color', 'no-plan-button-text-size', 'no-plan-button-text-weight', 'no-plan-button-border-radius',
      'no-plan-button-border-width', 'no-plan-button-border-type', 'no-plan-button-border-color',
      'no-plan-button-border-gradient-start', 'no-plan-button-border-gradient-end', 'no-plan-button-border-gradient-angle',
      'no-plan-button-icon-size', 'no-plan-container-padding', 'no-plan-container-bg-color',
      'no-plan-container-bg-transparent', 'no-plan-container-border-radius', 'no-plan-content-gap'
    ];
  }

  attributeChangedCallback() {
    this.render();
  }

  // Get ordered fields
  getOrderedFields() {
    const fields = [];

    // Helper to check if value exists and is not empty/null/undefined
    const hasValue = (value) => {
      return value && value !== 'null' && value !== 'undefined' && value.trim() !== '';
    };

    const planName = this.getAttribute('plan-name');
    if (this.getAttribute('show-plan-name') === 'true' && hasValue(planName)) {
      fields.push({
        type: 'plan-name',
        order: parseInt(this.getAttribute('plan-name-order') || '1'),
        label: this.getAttribute('label-plan-name') || 'Plan',
        value: planName
      });
    }

    const dataAmount = this.getAttribute('data-amount');
    if (this.getAttribute('show-data-amount') === 'true' && hasValue(dataAmount)) {
      fields.push({
        type: 'data-amount',
        order: parseInt(this.getAttribute('data-amount-order') || '2'),
        label: this.getAttribute('label-data-amount') || 'Data',
        value: dataAmount
      });
    }

    const speed = this.getAttribute('speed');
    if (this.getAttribute('show-speed') === 'true' && hasValue(speed)) {
      fields.push({
        type: 'speed',
        order: parseInt(this.getAttribute('speed-order') || '3'),
        label: this.getAttribute('label-speed') || 'Speed',
        value: speed
      });
    }

    const textAllowance = this.getAttribute('text-allowance');
    if (this.getAttribute('show-text-allowance') === 'true' && hasValue(textAllowance)) {
      fields.push({
        type: 'text-allowance',
        order: parseInt(this.getAttribute('text-allowance-order') || '4'),
        label: this.getAttribute('label-text-allowance') || 'SMS',
        value: textAllowance
      });
    }

    const voiceAllowance = this.getAttribute('voice-allowance');
    if (this.getAttribute('show-voice-allowance') === 'true' && hasValue(voiceAllowance)) {
      fields.push({
        type: 'voice-allowance',
        order: parseInt(this.getAttribute('voice-allowance-order') || '5'),
        label: this.getAttribute('label-voice-allowance') || 'Voice',
        value: voiceAllowance
      });
    }

    const autorenewPeriod = this.getAttribute('autorenew-period');
    if (this.getAttribute('show-autorenew-period') === 'true' && hasValue(autorenewPeriod)) {
      fields.push({
        type: 'autorenew-period',
        order: parseInt(this.getAttribute('autorenew-period-order') || '6'),
        label: '',
        value: autorenewPeriod
      });
    }

    // Sort by order
    fields.sort((a, b) => a.order - b.order);

    return fields;
  }

  render() {
    // Styling attributes
    const cardTitle = this.getAttribute('card-title') || '';
    const cardTitleTextColor = this.getAttribute('card-title-text-color') || '#000000';
    const cardTitleTextSize = this.getAttribute('card-title-text-size') || '20';
    const cardTitleTextWeight = this.getAttribute('card-title-text-weight') || '600';
    const containerBgColor = this.getAttribute('container-bg-color') || '#FFFFFF';
    const containerBgTransparent = this.getAttribute('container-bg-transparent') === 'true';
    const containerPadding = this.getAttribute('container-padding') || '20';
    const containerBorderRadius = this.getAttribute('container-border-radius') || '8';
    const textColor = this.getAttribute('text-color') || '#000000';
    const textSize = this.getAttribute('text-size') || '16';
    const textWeight = this.getAttribute('text-weight') || '400';
    const listGap = this.getAttribute('list-gap') || '12';
    const showBullet = this.getAttribute('show-bullet') !== 'false';
    const bulletType = this.getAttribute('bullet-type') || 'disc';

    // No-plan mode attributes
    const noPlanMode = this.getAttribute('no-plan-mode') === 'true';
    const noPlanIcon = this.getAttribute('no-plan-icon') || 'calendar_add_on';
    const noPlanIconSize = this.getAttribute('no-plan-icon-size') || '48';
    const noPlanIconColor = this.getAttribute('no-plan-icon-color') || '#5b4db3';
    const noPlanTitle = this.getAttribute('no-plan-title') || 'No Plan';
    const noPlanTitleSize = this.getAttribute('no-plan-title-size') || '24';
    const noPlanTitleWeight = this.getAttribute('no-plan-title-weight') || '600';
    const noPlanTitleColor = this.getAttribute('no-plan-title-color') || '#5b4db3';
    const noPlanText = this.getAttribute('no-plan-text') || 'Add a plan to get started';
    const noPlanTextShow = this.getAttribute('no-plan-text-show') !== 'false';
    const noPlanTextSize = this.getAttribute('no-plan-text-size') || '16';
    const noPlanTextWeight = this.getAttribute('no-plan-text-weight') || '400';
    const noPlanTextColor = this.getAttribute('no-plan-text-color') || '#000000';
    const noPlanTextMaxWidth = this.getAttribute('no-plan-text-max-width') || '500';
    const noPlanButtonUrl = this.getAttribute('no-plan-button-url') || '/';
    const noPlanButtonText = this.getAttribute('no-plan-button-text') || 'Add Plan';
    const noPlanButtonIconShow = this.getAttribute('no-plan-button-icon-show') !== 'false';
    const noPlanButtonGap = this.getAttribute('no-plan-button-gap') || '10';
    const noPlanButtonPaddingV = this.getAttribute('no-plan-button-padding-v') || '12';
    const noPlanButtonPaddingH = this.getAttribute('no-plan-button-padding-h') || '24';
    const noPlanButtonBg = this.getAttribute('no-plan-button-bg') || '#5b4db3';
    const noPlanButtonBgTransparent = this.getAttribute('no-plan-button-bg-transparent') === 'true';
    const noPlanButtonTextColor = this.getAttribute('no-plan-button-text-color') || '#FFFFFF';
    const noPlanButtonTextSize = this.getAttribute('no-plan-button-text-size') || '16';
    const noPlanButtonTextWeight = this.getAttribute('no-plan-button-text-weight') || '500';
    const noPlanButtonBorderRadius = this.getAttribute('no-plan-button-border-radius') || '6';
    const noPlanButtonBorderWidth = this.getAttribute('no-plan-button-border-width') || '0';
    const noPlanButtonBorderType = this.getAttribute('no-plan-button-border-type') || 'solid';
    const noPlanButtonBorderColor = this.getAttribute('no-plan-button-border-color') || '#5b4db3';
    const noPlanButtonBorderGradientStart = this.getAttribute('no-plan-button-border-gradient-start') || '#5b4db3';
    const noPlanButtonBorderGradientEnd = this.getAttribute('no-plan-button-border-gradient-end') || '#8b7dd8';
    const noPlanButtonBorderGradientAngle = this.getAttribute('no-plan-button-border-gradient-angle') || '135';
    const noPlanButtonIconSize = this.getAttribute('no-plan-button-icon-size') || '20';
    const noPlanContainerPadding = this.getAttribute('no-plan-container-padding') || '40';
    const noPlanContainerBgColor = this.getAttribute('no-plan-container-bg-color') || '#FFFFFF';
    const noPlanContainerBgTransparent = this.getAttribute('no-plan-container-bg-transparent') === 'true';
    const noPlanContainerBorderRadius = this.getAttribute('no-plan-container-border-radius') || '8';
    const noPlanContentGap = this.getAttribute('no-plan-content-gap') || '16';

    const fields = this.getOrderedFields();

    this.shadowRoot.innerHTML = `
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200" />

      <style>
        :host {
          display: block;
          width: 100%;
        }

        .plan-details-card-title {
          font-size: ${cardTitleTextSize}px;
          font-weight: ${cardTitleTextWeight};
          color: ${cardTitleTextColor};
          margin: 0 0 12px 0;
        }

        .plan-details-container {
          background-color: ${containerBgTransparent ? 'transparent' : containerBgColor};
          padding: ${containerPadding}px;
          border-radius: ${containerBorderRadius}px;
          display: flex;
          flex-direction: column;
          min-width: 260px;
        }

        .plan-details-list {
          list-style-type: ${showBullet ? bulletType : 'none'};
          padding-left: ${showBullet ? '20px' : '0'};
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: ${listGap}px;
        }

        .plan-detail-item {
          font-size: ${textSize}px;
          font-weight: ${textWeight};
          color: ${textColor};
        }

        /* No Plan Styles */
        .no-plan-container {
          display: ${noPlanMode ? 'flex' : 'none'};
          flex-direction: column;
          justify-content: center;
          align-items: center;
          padding: ${noPlanContainerPadding}px;
          gap: ${noPlanContentGap}px;
          background-color: ${noPlanContainerBgTransparent ? 'transparent' : noPlanContainerBgColor};
          border-radius: ${noPlanContainerBorderRadius}px;
        }

        .no-plan-icon {
          font-family: 'Material Symbols Outlined';
          font-size: ${noPlanIconSize}px;
          color: ${noPlanIconColor};
        }

        .no-plan-title {
          font-size: ${noPlanTitleSize}px;
          font-weight: ${noPlanTitleWeight};
          color: ${noPlanTitleColor};
          text-align: center;
          margin: 0;
        }

        .no-plan-text {
          font-size: ${noPlanTextSize}px;
          font-weight: ${noPlanTextWeight};
          color: ${noPlanTextColor};
          text-align: center;
          margin: 0;
          max-width: ${noPlanTextMaxWidth}px;
        }

        .no-plan-button {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: ${noPlanButtonGap}px;
          padding: ${noPlanButtonPaddingV}px ${noPlanButtonPaddingH}px;
          ${noPlanButtonBgTransparent ? 'background-color: transparent;' : `background-color: ${noPlanButtonBg};`}
          color: ${noPlanButtonTextColor};
          font-size: ${noPlanButtonTextSize}px;
          font-weight: ${noPlanButtonTextWeight};
          border-radius: ${noPlanButtonBorderRadius}px;
          cursor: pointer;
          text-decoration: none;
          transition: opacity 0.2s ease;
          ${noPlanButtonBorderWidth > 0 && noPlanButtonBorderType === 'solid' ? `
            border: ${noPlanButtonBorderWidth}px solid ${noPlanButtonBorderColor};
          ` : ''}
          ${noPlanButtonBorderWidth > 0 && noPlanButtonBorderType === 'gradient' ? `
            border: ${noPlanButtonBorderWidth}px solid transparent;
            background-image:
              linear-gradient(${noPlanButtonBgTransparent ? 'transparent' : noPlanButtonBg}, ${noPlanButtonBgTransparent ? 'transparent' : noPlanButtonBg}),
              linear-gradient(${noPlanButtonBorderGradientAngle}deg, ${noPlanButtonBorderGradientStart}, ${noPlanButtonBorderGradientEnd});
            background-origin: padding-box, border-box;
            background-clip: padding-box, border-box;
          ` : ''}
          ${noPlanButtonBorderWidth <= 0 ? 'border: none;' : ''}
        }

        .no-plan-button:hover {
          opacity: 0.9;
        }

        .no-plan-button .material-symbols-outlined {
          font-size: ${noPlanButtonIconSize}px;
        }
      </style>

      ${cardTitle ? `<h3 class="plan-details-card-title">${cardTitle}</h3>` : ''}

      <div class="plan-details-container" style="display: ${noPlanMode ? 'none' : 'flex'}">
        <ul class="plan-details-list">
          ${fields.map(field => `
            <li class="plan-detail-item">${field.value}</li>
          `).join('')}
        </ul>
      </div>

      <div class="no-plan-container">
        <span class="no-plan-icon material-symbols-outlined">${noPlanIcon}</span>
        <h3 class="no-plan-title">${noPlanTitle}</h3>
        ${noPlanTextShow ? `<p class="no-plan-text">${noPlanText}</p>` : ''}
        <a href="#" class="no-plan-button" data-action="add-plan">
          ${noPlanButtonIconShow ? '<span class="material-symbols-outlined">add</span>' : ''}
          <span>${noPlanButtonText}</span>
        </a>
      </div>
    `;

    // Add event listener for Add Plan button
    const addPlanButton = this.shadowRoot.querySelector('.no-plan-button[data-action="add-plan"]');
    if (addPlanButton) {
      // Remove existing listener if any
      addPlanButton.removeEventListener('click', this.handleAddPlanClick);
      // Add new listener
      this.handleAddPlanClick = (e) => {
        e.preventDefault();
        
        // Get current eSIM data
        let currentEsimData = null;
        
        // Try to get from window.accountEsims (check selected eSIM first)
        if (window.accountEsims && window.accountEsims.length > 0) {
          const dropdown = document.getElementById('esim_selector_dropdown');
          if (dropdown && dropdown.value) {
            currentEsimData = window.accountEsims.find(e => e.esim?.iccid === dropdown.value);
          }
          
          // Fallback to first eSIM if no selection
          if (!currentEsimData) {
            currentEsimData = window.accountEsims[0];
          }
        }

        if (!currentEsimData || !currentEsimData.esim || !currentEsimData.esim.iccid) {
          console.error('No eSIM data available for Add Plan');
          return;
        }

        // Check if addPlanToEsim function exists
        if (typeof window.addPlanToEsim !== 'function') {
          console.error('addPlanToEsim function not found');
          return;
        }

        // Call addPlanToEsim function
        window.addPlanToEsim(currentEsimData.esim.iccid, currentEsimData.info);
      };
      
      addPlanButton.addEventListener('click', this.handleAddPlanClick);
    }
  }

  // Public method to update plan data
  updatePlanData(planData) {
    // Set attributes only if they have actual values (not null/undefined)
    if (planData.planName) {
      this.setAttribute('plan-name', planData.planName);
    } else {
      this.setAttribute('plan-name', '');
    }

    if (planData.dataAmount) {
      this.setAttribute('data-amount', planData.dataAmount);
    } else {
      this.setAttribute('data-amount', '');
    }

    if (planData.speed) {
      this.setAttribute('speed', planData.speed);
    } else {
      this.setAttribute('speed', '');
    }

    if (planData.textAllowance) {
      this.setAttribute('text-allowance', planData.textAllowance);
    } else {
      this.setAttribute('text-allowance', '');
    }

    if (planData.voiceAllowance) {
      this.setAttribute('voice-allowance', planData.voiceAllowance);
    } else {
      this.setAttribute('voice-allowance', '');
    }

    if (planData.autorenewPeriod) {
      this.setAttribute('autorenew-period', planData.autorenewPeriod);
    } else {
      this.setAttribute('autorenew-period', '');
    }
  }
}

// Register the custom element
customElements.define('plan-details-card', PlanDetailsCard);
